<template>
  <component :is="isComponent ? 'div' : 'UPage'">
    <ConfigHeader
      v-if="!isComponent"
      :back-to="`/customers/${customerNo}/tenants`"
      :saving="savingConfig"
      :tenant-name="tenantName"
      title="Workflow Konfig"
      @save="toggleWorkflowActive(workflowActive)"
    />

    <component :is="isComponent ? 'div' : 'UPageBody'" :class="{ 'p-0': isComponent }">
      <div v-if="loading" class="flex justify-center py-6">
        <UIcon class="w-8 h-8 animate-spin text-muted" name="i-heroicons-arrow-path" />
      </div>

      <div v-else class="space-y-6">
        <!-- Workflow Active Toggle -->
        <ConfigStatusCard
          v-model="workflowActive"
          :disabled="!canWrite"
          :loading="savingConfig"
          title="Workflow Automatisierung"
          description="Aktivieren oder deaktivieren Sie die Workflow-Engine für diesen Mandanten."
          icon="i-lucide-workflow"
          @update:model-value="toggleWorkflowActive"
        />

        <!-- E-Mail-Feld des microtech-Benutzers -->
        <UCard>
          <div class="space-y-4 max-w-xl">
            <UFormField
              label="E-Mail-Feld des microtech-Benutzers"
              description="Bestimmt, welches E-Mail-Feld des microtech-Benutzerdatensatzes für Rückfragen bei Workflow-Aufgaben verwendet wird."
            >
              <USelect
                v-model="userEmailField"
                :disabled="!canWrite || savingConfig"
                :items="USER_EMAIL_FIELD_OPTIONS"
                class="w-full"
                label-key="label"
                value-key="value"
              />
            </UFormField>
          </div>
        </UCard>

        <USeparator class="my-8" />

        <ConfigChangelogAlert
          description="Bitte stellen Sie sicher, dass Ihre Workflow-Konfiguration die aktuellen Systemanforderungen erfüllt."
        />

        <!-- Einrichtungs-Informationen -->
        <UCard
          :ui="{
            root: isInfoOpen ? '' : 'divide-y-0',
            body: isInfoOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isInfoOpen = !isInfoOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-info" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Einrichtung und Konfiguration testen</h3>
              </div>
              <UButton
                :icon="isInfoOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                color="neutral"
                variant="ghost"
                @click.stop="isInfoOpen = !isInfoOpen"
              />
            </div>
          </template>

          <div v-show="isInfoOpen" class="space-y-6">
            <ConfigDocsAlert
              title="Einrichtung der Workflow Komponente"
              description="Folgen Sie bitte den Anweisungen in unserer Konfigurations-Dokumentation, um die Workflow-Komponente erfolgreich zu konfigurieren."
              doc-key="system-config.workflow.microtech-configuration"
              tenant-doc-key="tenant-config.workflow-portal-config"
            />

            <UAlert
              icon="i-lucide-lightbulb"
              title="Aktivierung"
              description="Es wird empfohlen, die Komponente erst nach einem erfolgreichen Konfigurationstest zu aktivieren."
              color="warning"
              variant="subtle"
              :ui="{
                title: 'text-warning-900 dark:text-warning-200',
                description: 'text-warning-800 dark:text-warning-300',
              }"
            />
          </div>
        </UCard>

        <!-- Workflow Blueprints -->
        <UCard
          :ui="{
            root: isBlueprintsOpen ? '' : 'divide-y-0',
            body: isBlueprintsOpen ? 'p-0 sm:p-0' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
          class="overflow-x-auto"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isBlueprintsOpen = !isBlueprintsOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-workflow" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Workflow Blueprints</h3>
              </div>
              <div class="flex items-center gap-2">
                <UButton
                  v-if="canWrite && isBlueprintsOpen"
                  color="primary"
                  icon="i-heroicons-plus"
                  label="Neuer Blueprint"
                  class="shrink-0"
                  @click.stop="router.push('/admin/workflow/new')"
                />
                <UButton
                  :icon="isBlueprintsOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                  color="neutral"
                  variant="ghost"
                  @click.stop="isBlueprintsOpen = !isBlueprintsOpen"
                />
              </div>
            </div>
          </template>

          <UTable
            v-if="isBlueprintsOpen"
            :columns="workflowColumns"
            :data="blueprints"
            :loading="loading"
            class="w-full min-w-[600px] sm:min-w-0"
          >
            <template #id-cell="{ row }">
              <div class="flex items-center gap-2">
                <span class="font-mono text-sm">{{ row.original.id }}</span>
                <UBadge v-if="row.original.version" color="neutral" size="sm" variant="subtle">
                  v{{ row.original.version }}
                </UBadge>
              </div>
            </template>

            <template #steps-cell="{ row }">
              <UBadge color="neutral" variant="subtle">
                {{ row.original.steps?.length || 0 }} Schritte
              </UBadge>
            </template>

            <template #status-cell="{ row }">
              <UBadge v-if="isActive(row.original)" color="success" variant="subtle">aktiv</UBadge>
              <UBadge v-else color="error" variant="subtle">inaktiv</UBadge>
            </template>

            <template #actions-cell="{ row }">
              <div class="flex justify-end px-2 gap-2">
                <UButton
                  color="neutral"
                  icon="i-heroicons-eye"
                  variant="ghost"
                  @click="router.push(`/admin/workflow/${row.original.id}`)"
                />
                <UButton
                  v-if="canWrite"
                  color="neutral"
                  icon="i-heroicons-pencil-square"
                  variant="ghost"
                  @click="router.push(`/admin/workflow/${row.original.id}/edit`)"
                />
                <UButton
                  v-if="canWrite"
                  color="neutral"
                  icon="i-heroicons-trash"
                  variant="ghost"
                  @click="deleteBlueprint(row.original.id)"
                />
              </div>
            </template>
          </UTable>
        </UCard>

        <!-- Konfigurationstest -->
        <UCard
          :ui="{
            root: isExpertValuesOpen ? '' : 'divide-y-0',
            body: isExpertValuesOpen ? 'p-6' : 'hidden',
            header: 'px-4 py-3 sm:px-6',
          }"
        >
          <template #header>
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="isExpertValuesOpen = !isExpertValuesOpen"
            >
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-clipboard-check" class="h-5 w-5 text-primary" />
                <h3 class="text-base font-semibold">Konfigurationstest</h3>
              </div>
              <UButton
                :icon="isExpertValuesOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                color="neutral"
                variant="ghost"
                @click.stop="isExpertValuesOpen = !isExpertValuesOpen"
              />
            </div>
          </template>

          <div v-show="isExpertValuesOpen" class="space-y-6">
            <div
              class="flex flex-col sm:flex-row sm:items-center gap-6 p-4 rounded-lg border border-primary-200 bg-primary-50/50 dark:border-primary-800 dark:bg-primary-900/20"
            >
              <div class="flex-1">
                <h4 class="font-bold text-sm">Konfiguration testen</h4>
                <p class="text-xs text-muted mt-1">
                  Überprüfen Sie, ob die benötigten Felder für den Workflow in microtech vorhanden sind.
                </p>
              </div>
              <UButton
                v-if="canWrite"
                :loading="testingExpertValues"
                color="primary"
                icon="i-lucide-zap"
                label="Test starten"
                @click="testExpertValues"
              />
            </div>

            <div
              v-if="expertTestResult"
              class="rounded-lg border p-4 transition-all duration-300"
              :class="
                expertTestResult.success
                  ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                  : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
              "
            >
              <div class="flex items-center gap-3 mb-6">
                <UIcon
                  :name="expertTestResult.success ? 'i-lucide-check-circle' : 'i-lucide-x-circle'"
                  :class="expertTestResult.success ? 'text-green-500' : 'text-red-500'"
                  class="w-8 h-8"
                />
                <span
                  :class="
                    expertTestResult.success
                      ? 'text-green-700 dark:text-green-300'
                      : 'text-red-700 dark:text-red-300'
                  "
                  class="font-bold text-lg"
                >
                  {{ expertTestResult.message }}
                </span>
              </div>

              <div
                v-if="expertTestResult.checks"
                class="grid gap-4 sm:grid-cols-2 mt-6 border-t border-gray-200 dark:border-gray-800 pt-6"
              >
                <div
                  v-for="check in expertTestResult.checks"
                  :key="check.label"
                  class="flex gap-4 p-4 rounded-lg bg-white/50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800"
                >
                  <div class="mt-0.5 shrink-0">
                    <UIcon
                      :name="
                        check.status === 'success'
                          ? 'i-lucide-check-circle'
                          : check.status === 'warning'
                            ? 'i-lucide-alert-circle'
                            : check.status === 'neutral'
                              ? 'i-lucide-help-circle'
                              : 'i-lucide-x-circle'
                      "
                      :class="
                        check.status === 'success'
                          ? 'text-green-500'
                          : check.status === 'warning'
                            ? 'text-orange-500'
                            : check.status === 'neutral'
                              ? 'text-gray-400'
                              : 'text-red-500'
                      "
                      class="w-5 h-5 flex-shrink-0"
                    />
                  </div>
                  <div>
                    <p class="font-bold text-gray-900 dark:text-white text-sm">{{ check.label }}</p>
                    <p class="text-gray-600 dark:text-gray-400 text-xs mt-1 leading-relaxed">
                      {{ check.detail }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </UCard>
      </div>
    </component>

    <UModal
      v-model:open="showDeleteModal"
      title="Blueprint löschen?"
      description="Sind Sie sicher, dass Sie diesen Blueprint wirklich löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden."
    >
      <template #title>
        <div class="flex items-center gap-2">
          <UIcon name="i-heroicons-trash" class="w-5 h-5 text-error" />
          <span>Blueprint löschen?</span>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end gap-2">
          <UButton
            color="neutral"
            variant="ghost"
            label="Abbrechen"
            @click="showDeleteModal = false"
          />
          <UButton color="error" label="Löschen" @click="confirmDelete" />
        </div>
      </template>
    </UModal>

  </component>
</template>

<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import type { WorkflowBlueprint, TestCheck, UserEmailField } from '@/types'
import { USER_EMAIL_FIELD_OPTIONS } from '@/types'
import ConfigHeader from '@/components/ConfigHeader.vue'

defineProps({
  customerNo: {
    type: String,
    required: true,
  },
  tenantName: {
    type: String,
    required: true,
  },
  isComponent: {
    type: Boolean,
    default: false,
  },
  canWrite: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['dirty'])

const router = useRouter()
const toast = useToast()
const loading = ref(true)
const blueprints = ref<WorkflowBlueprint[]>([])
const workflowActive = ref(false)
const userEmailField = ref<UserEmailField>('fldEMail1')
const savingConfig = ref(false)

const isInfoOpen = ref(false)
const isBlueprintsOpen = ref(true)
const isExpertValuesOpen = ref(false)
const testingExpertValues = ref(false)
const expertTestResult = ref<{ success: boolean; message: string; checks?: TestCheck[] } | null>(null)

const showDeleteModal = ref(false)
const blueprintToDelete = ref<string | null>(null)

defineExpose({
  saveConfig: () => toggleWorkflowActive(workflowActive.value),
})

const isActive = (blueprint: WorkflowBlueprint) => {
  const hasSteps = blueprint.steps && blueprint.steps.length > 0
  const triggerConditionFalse = blueprint.trigger?.nextStep?.condition === 'false'
  return hasSteps && !triggerConditionFalse
}

const workflowColumns = [
  { accessorKey: 'id', header: 'Blueprint ID', id: 'id' },
  { id: 'steps', header: 'Schritte' },
  { id: 'status', header: 'Status' },
  { id: 'actions', header: '' },
]

const fetchBlueprints = async () => {
  loading.value = true
  try {
    const [blueprintsRes, configRes] = await Promise.all([
      axios.get('/api/workflow/v1/blueprints'),
      axios.get('/api/workflow/v1/config'),
    ])
    blueprints.value = blueprintsRes.data
    workflowActive.value = configRes.data.active
    userEmailField.value = configRes.data.userEmailField || 'fldEMail1'
  } catch (error) {
    console.error('Failed to fetch data', error)
    if (axios.isAxiosError(error) && error.response?.status !== 404) {
      toast.add({ title: 'Fehler', description: 'Fehler beim Laden der Daten.', color: 'danger' })
    }
  } finally {
    loading.value = false
  }
}

const toggleWorkflowActive = async (active: boolean) => {
  savingConfig.value = true
  try {
    await axios.put('/api/workflow/v1/config', {
      active: active,
      userEmailField: userEmailField.value,
    })
    toast.add({
      title: 'Konfiguration gespeichert',
      description: `Workflow-Automatisierung wurde ${active ? 'aktiviert' : 'deaktiviert'}.`,
      color: 'success',
    })
  } catch (error) {
    console.error('Failed to save workflow config', error)
    workflowActive.value = !active // revert on error
    toast.add({
      title: 'Fehler',
      description: 'Fehler beim Speichern der Konfiguration.',
      color: 'danger',
    })
  } finally {
    savingConfig.value = false
  }
}

const deleteBlueprint = (id: string) => {
  blueprintToDelete.value = id
  showDeleteModal.value = true
}

const testExpertValues = async () => {
  testingExpertValues.value = true
  expertTestResult.value = null
  try {
    const response = await axios.post('/api/workflow/v1/config/validate')
    expertTestResult.value = {
      success: response.data.ok,
      message: response.data.message,
      checks: response.data.checks,
    }
  } catch (error: unknown) {
    let msg = 'Unbekannter Fehler'
    let checks: TestCheck[] | undefined = undefined
    if (axios.isAxiosError(error)) {
      if (error.response?.data && typeof error.response.data === 'object') {
        const data = error.response.data as { message?: string; checks?: TestCheck[] }
        msg = data.message || error.message
        checks = data.checks
      } else {
        msg = error.response?.data || error.message
      }
    } else if (error instanceof Error) {
      msg = error.message
    }
    expertTestResult.value = { success: false, message: `Fehler: ${msg}`, checks }
  } finally {
    testingExpertValues.value = false
  }
}

const confirmDelete = async () => {
  if (!blueprintToDelete.value) return
  const id = blueprintToDelete.value
  showDeleteModal.value = false

  try {
    await axios.delete(`/api/workflow/v1/blueprints/${id}`)
    toast.add({
      title: 'Blueprint gelöscht',
      description: `Der Blueprint "${id}" wurde erfolgreich gelöscht.`,
      color: 'success',
    })
    fetchBlueprints() // Refresh the list
  } catch (error) {
    console.error('Failed to delete blueprint', error)
    toast.add({
      title: 'Fehler',
      description: 'Fehler beim Löschen des Blueprints.',
      color: 'danger',
    })
  }
}

watch(workflowActive, () => {
  emit('dirty')
})

watch(userEmailField, () => {
  emit('dirty')
})

onMounted(() => {
  fetchBlueprints()
})
</script>
